'use client';

import {
	Box, defineStyle, Field,
} from '@chakra-ui/react';
import { useFormContext, useWatch } from 'react-hook-form';

import { useFormError } from '../../../hooks';
import type { FormFieldProps, FormValues } from '../../../types';

type CounterProps = {
	maxChars?: number
};

export const FormFloatingFieldText = <T extends FormValues>(props: FormFieldProps<T> & CounterProps) => {
	const {
		children,
		helperText,
		label,
		maxChars,
		name,
		optionalText,
		...rest
	} = props;

	const { control } = useFormContext<T>();
	const value = useWatch({ control, name: name }) ?? '';

	const count = Array.from(String(value)).length; // unicode-safe char count

	const floatingStyles = defineStyle({
		_peerFocusVisible:     { color: 'fg', insetStart: '2', top: '-3' },
		_peerPlaceholderShown: { color: 'fg.muted', insetStart: '3', top: '2.5' },
		bg:                    'bg',
		fontWeight:            'normal',
		insetStart:            '2',
		pointerEvents:         'none',
		pos:                   'absolute',
		px:                    '0.5',
		top:                   '-3',
		transition:            'position',
	});

	const { errorText, isError, isSubmitting } = useFormError(name);
	const Component = isError ? Field.ErrorText : Field.HelperText;
	const text = isError ? errorText : helperText;

	return (
		<Field.Root disabled={isSubmitting} invalid={isError} {...rest}>
			<Box pos="relative" w="full">
				{children}
				<Field.Label css={floatingStyles}>
					{label}
					<Field.RequiredIndicator fallback={optionalText} />
				</Field.Label>
			</Box>

			{(text || maxChars) && (
				<Box
					alignItems="center"
					display="flex"
					justifyContent="space-between"
					w="full"
				>
					<Component minH="14px" ml={1} textStyle="caption">
						{text ?? ''}
					</Component>
					{maxChars && (
						<Box color="fg.muted" mr={1} textStyle="caption">
							{count}
							{typeof maxChars === 'number' ? `/${maxChars}` : null}
						</Box>
					)}
				</Box>
			)}
		</Field.Root>
	);
};
